import React from 'react';
import { graphql } from 'gatsby';
import Layout from '@components/Layout/Layout';
import Seo from '@components/SEO/SEO';
import HeroSubpage from '@components/HeroSubpage/HeroSubpage';
import TerminalCTA from '@components/TerminalCTA/TerminalCTA';
import Markdown from 'react-markdown';
import formatDate from '@prismic/utils/formatDate';

import css from './ProgramaticSEO.module.scss';
import PrismicLink from '../../components/PrismicLink/PrismicLink';

const ProgramaticSEOTemplate = ({ location, data }) => {
  const doc = data?.airtable?.data;
  const author = data?.prismicPerson;
  let moreLinks = [];

  // search for links above the row
  moreLinks = data?.allAirtable?.edges
    .filter(item => item.node.rowIndex < data.airtable.rowIndex)
    .slice(-4);

  // if fewer than 4 links, search for links after the row (up to 4)
  if (moreLinks.length < 4) {
    moreLinks = moreLinks.concat(
      data?.allAirtable?.edges
        .filter(item => item.node.rowIndex > data.airtable.rowIndex)
        .slice(0, moreLinks.length)
    );
  }

  if (doc) {
    return (
      <Layout page="ktp" location={location}>
        <Seo
          title={doc.SEO_title}
          description={doc.SEO_description}
          linkRel={`https://www.sharetribe.com${doc.folder}${doc.URL}/`}
          socialTitle={doc.SEO_title}
          socialDescription={doc.SEO_description}
        />
        <HeroSubpage
          subtitle={
            <>
              {!!author ? (
                <PrismicLink className={css.author} to={author.url}>
                  By {author.data.name} ·{' '}
                </PrismicLink>
              ) : null}
              <span>{formatDate(doc.Published_Date)}</span>
            </>
          }
          title={doc.H1}
          ingress={doc.H1P}
        />
        <section className={css.content}>
          {doc.Youtube ? (
            <div className={css.video}>
              <iframe
                src={doc.Youtube}
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              ></iframe>
            </div>
          ) : null}

          <h2>{doc.H2}</h2>
          <div>
            <Markdown>{doc.H21P1}</Markdown>
          </div>
          <div>
            <Markdown>{doc.H21P2}</Markdown>
          </div>
          <h2>{doc.H22}</h2>
          <div>
            <Markdown>{doc.H22P1}</Markdown>
          </div>
          <div>
            <Markdown>{doc.H22P2}</Markdown>
          </div>
          <h3>{doc.H31}</h3>
          <div>
            <Markdown>{doc.H31P1}</Markdown>
          </div>
          <div>
            <Markdown>{doc.H31P2}</Markdown>
          </div>
          <h3>{doc.H32}</h3>
          <div>
            <Markdown>{doc.H32P1}</Markdown>
          </div>
          <div>
            <Markdown>{doc.H32P2}</Markdown>
          </div>
          <h2>{doc.H23}</h2>
          <div>
            <Markdown>{doc.H23P1}</Markdown>
          </div>
          <h3>{doc.H34}</h3>
          <div>
            <Markdown>{doc.H34P1}</Markdown>
          </div>
          <h2>{doc.H24}</h2>
          <div>
            <Markdown>{doc.H24P1}</Markdown>
          </div>
          <h3>{doc.H35}</h3>
          <div>
            {moreLinks.length ? (
              <ul>
                {moreLinks.map(item => {
                  return (
                    <li key={item.node.data.URL}>
                      <PrismicLink
                        className={css.readMore}
                        to={`${item.node.data.folder}${item.node.data.URL}/`}
                      >
                        {item.node.data.H1}
                      </PrismicLink>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </section>
        <TerminalCTA />
      </Layout>
    );
  }
};

export const query = graphql`
  query ProgramaticSEO($id: String!, $authorSlug: String, $contentCluster: String) {
    airtable(id: { eq: $id }) {
      id
      rowIndex
      data {
        URL
        H1P
        H1
        H1P
        Youtube
        H2
        H21P1
        H21P2
        H22
        H22P1
        H22P2
        H23
        H23P1
        H24
        H24P1
        H31
        H31P1
        H31P2
        H32
        H32P1
        H32P2
        H34
        H34P1
        H35
        H35P1
        SEO_description
        SEO_title
        Name
        folder
        Published_Date
        Author
        Content_Cluster
      }
    }
    allAirtable(
      filter: { data: { Content_Cluster: { eq: $contentCluster }, Published: { eq: "yes" } } }
      sort: { rowIndex: ASC }
    ) {
      edges {
        node {
          data {
            URL
            H1
            folder
          }
          rowIndex
        }
      }
    }
    prismicPerson(uid: { eq: $authorSlug }) {
      data {
        schema_json {
          structured_data {
            text
          }
        }
        seo_description
        seo_og_description
        seo_og_image {
          url
          alt
        }
        seo_og_title
        seo_title
        name
        position
        image {
          gatsbyImageData(width: 168, height: 168, placeholder: NONE)
          alt
        }
        social {
          text
          link {
            url
          }
        }
      }
      uid
      url
      type
    }
  }
`;

export default ProgramaticSEOTemplate;
